import React, { useEffect, useState, useCallback } from 'react';
import { fetchJson } from './api';
import { useTranslation } from 'react-i18next';

const GOLD = '#d4a550';
const GOLD_DIM = 'rgba(212,165,80,0.13)';
const GOLD_MID = 'rgba(212,165,80,0.25)';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT_DIM = 'rgba(232,230,223,0.5)';
const TEXT_MED = 'rgba(232,230,223,0.78)';
const BG = '#0d0d14';
const BG2 = '#13131f';
const BG3 = '#1a1a28';
const GREEN = '#4ade80';
const GREEN_DIM = 'rgba(74,222,128,0.13)';
const RED = '#f87171';

function todayISO() { return new Date().toISOString().slice(0,10); }
function toISO(d) { if (!d) return ''; return typeof d==='string' ? d.slice(0,10) : new Date(d).toISOString().slice(0,10); }
function diffDays(a,b) { return Math.round((new Date(b)-new Date(a))/86400000); }
function fmtDate(iso, lang='it') {
  if (!iso) return '-';
  return new Date(iso+'T00:00:00').toLocaleDateString(lang==='en'?'en-GB':'it-IT',{day:'numeric',month:'short'});
}
function fmtDateLong(iso, lang='it') {
  if (!iso) return '-';
  return new Date(iso+'T00:00:00').toLocaleDateString(lang==='en'?'en-GB':'it-IT',{day:'numeric',month:'long',year:'numeric'});
}
function blockStatus(b) {
  const t=todayISO(), s=toISO(b.start_date), e=toISO(b.end_date);
  if (e < t) return 'past';
  if (s <= t) return 'active';
  return 'future';
}
function groupByProduction(blocks) {
  const map = {};
  for (const b of blocks) {
    if (!map[b.production_id]) map[b.production_id] = {
      production_id: b.production_id, production_name: b.production_name,
      plan_name: b.plan_name, master_delivery: b.master_delivery, blocks: []
    };
    map[b.production_id].blocks.push(b);
  }
  return Object.values(map);
}
function isProdDone(prod) {
  const t=todayISO();
  return prod.blocks.every(b=>toISO(b.end_date)<t);
}

function ActiveHeroCard({ block, prodName, onComplete, completing }) {
  const { t: tr, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const t = todayISO();
  const start = toISO(block.start_date);
  const end = toISO(block.end_date);
  const daysLeft = diffDays(t, end);
  const totalDays = block.dur_work || Math.max(1, diffDays(start,end)+1);
  const elapsed = Math.max(0, diffDays(start,t));
  const pct = Math.min(100, Math.max(0, Math.round((elapsed/totalDays)*100)));
  const urgent = daysLeft <= 1;
  const accentColor = urgent ? RED : GOLD;
  return (
    <div style={{background:'linear-gradient(135deg,rgba(212,165,80,0.09) 0%,rgba(212,165,80,0.03) 100%)',
      border:`1px solid ${urgent?'rgba(248,113,113,0.5)':'rgba(212,165,80,0.4)'}`,
      borderRadius:16,padding:'28px 32px',position:'relative',overflow:'hidden'}}>
      <div style={{position:'absolute',top:0,left:0,right:0,height:3,
        background:`linear-gradient(90deg,${accentColor},transparent)`,borderRadius:'16px 16px 0 0'}}/>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:20}}>
        <div style={{flex:1,minWidth:220}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
            <span style={{background:urgent?'rgba(248,113,113,0.2)':'rgba(212,165,80,0.2)',
              color:accentColor,fontSize:11,fontWeight:700,letterSpacing:'0.1em',
              padding:'3px 10px',borderRadius:20,textTransform:'uppercase'}}>
              {urgent?(daysLeft<0?tr('piano.status_expired'):tr('piano.status_today')):tr('piano.status_active')}
            </span>
            <span style={{color:TEXT_DIM,fontSize:12}}>{prodName}</span>
          </div>
          <div style={{color:'#fff',fontWeight:700,fontSize:22,marginBottom:4,lineHeight:1.2}}>
            {block.phase_name}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{background:'rgba(255,255,255,0.1)',color:'#fff',fontSize:12,
              fontWeight:600,padding:'2px 10px',borderRadius:20}}>EP.{block.episode_no}</span>
            {block.part_label&&<span style={{color:TEXT_MED,fontSize:13}}>{block.part_label}</span>}
          </div>
          <div style={{marginTop:14,color:TEXT_MED,fontSize:13,display:'flex',gap:16,flexWrap:'wrap',alignItems:'center'}}>
            <span>📅 {fmtDate(start,lang)} → {fmtDate(end,lang)}</span>
            <span style={{color:accentColor,fontWeight:600}}>
              {daysLeft<0?`${Math.abs(daysLeft)}${tr('piano.days_late')}`:daysLeft===0?tr('piano.last_day'):`${daysLeft}${tr('piano.days_left')}`}
            </span>
          </div>
          <div style={{marginTop:14}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{color:TEXT_DIM,fontSize:11}}>{tr('piano.progress')}</span>
              <span style={{color:accentColor,fontSize:11,fontWeight:600}}>{pct}%</span>
            </div>
            <div style={{height:6,background:'rgba(255,255,255,0.07)',borderRadius:3,overflow:'hidden'}}>
              <div style={{height:'100%',width:`${pct}%`,
                background:`linear-gradient(90deg,${accentColor},${urgent?'#fca5a5':'#e8c97a'})`,
                borderRadius:3,transition:'width 0.6s ease'}}/>
            </div>
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:12}}>
          <button onClick={()=>onComplete(block.id)} disabled={completing===block.id}
            style={{padding:'10px 22px',
              background:completing===block.id?'rgba(255,255,255,0.04)':GREEN_DIM,
              border:`1px solid ${GREEN}`,borderRadius:10,color:GREEN,
              fontSize:13,fontWeight:700,cursor:completing===block.id?'not-allowed':'pointer',
              whiteSpace:'nowrap'}}>
            {completing===block.id?tr('piano.completing'):tr('piano.complete_btn')}
          </button>
          <span style={{color:TEXT_DIM,fontSize:11,textAlign:'right'}}>{tr('piano.complete_note')}</span>
        </div>
      </div>
    </div>
  );
}

function FutureCard({ block, prodName }) {
  const { t: tr, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const t = todayISO();
  const start = toISO(block.start_date);
  const daysUntil = diffDays(t, start);
  const soon = daysUntil <= 7;
  return (
    <div style={{background:BG2,border:`1px solid ${soon?'rgba(212,165,80,0.25)':BORDER}`,
      borderRadius:12,padding:'16px 18px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:8}}>
        <span style={{color:soon?GOLD:'#fff',fontWeight:600,fontSize:14,lineHeight:1.3}}>
          {block.phase_name}
        </span>
        <span style={{background:'rgba(255,255,255,0.08)',color:TEXT_DIM,fontSize:11,
          fontWeight:600,padding:'2px 8px',borderRadius:20,whiteSpace:'nowrap',flexShrink:0}}>
          EP.{block.episode_no}
        </span>
      </div>
      {block.part_label&&<div style={{color:TEXT_DIM,fontSize:12,marginBottom:6}}>{block.part_label}</div>}
      <div style={{color:TEXT_DIM,fontSize:12,marginBottom:4}}>{prodName}</div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
        <span style={{color:TEXT_DIM,fontSize:12}}>{fmtDate(start,lang)} → {fmtDate(block.end_date,lang)}</span>
        <span style={{color:soon?GOLD:TEXT_DIM,fontSize:12,fontWeight:soon?600:400}}>{tr('piano.days_from_now')} {daysUntil}{tr('piano.days_unit')}</span>
      </div>
    </div>
  );
}

function PastCard({ block, prodName }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  return (
    <div style={{background:'rgba(255,255,255,0.015)',border:`1px solid ${BORDER}`,
      borderRadius:10,padding:'12px 16px',opacity:0.65,
      display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
      <div>
        <span style={{color:TEXT_MED,fontSize:13,fontWeight:600}}>{block.phase_name}</span>
        <span style={{color:TEXT_DIM,fontSize:12,marginLeft:8}}>EP.{block.episode_no}</span>
        {block.part_label&&<span style={{color:TEXT_DIM,fontSize:12,marginLeft:8}}>{block.part_label}</span>}
        <div style={{color:TEXT_DIM,fontSize:11,marginTop:2}}>{prodName}</div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{color:TEXT_DIM,fontSize:12}}>{fmtDate(block.start_date,lang)} → {fmtDate(block.end_date,lang)}</span>
        <span style={{background:GREEN_DIM,color:GREEN,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20}}>✓</span>
      </div>
    </div>
  );
}

export default function PianoDiPost() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [toast, setToast] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const pastRef = React.useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchJson('/postplans/my-schedule');
      setBlocks(data.blocks||[]);
      setError(null);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  },[]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>{const t=setInterval(load,60000);return()=>clearInterval(t);},[load]);

  const showToast=(msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3500);};

  const handleComplete=async(blockId)=>{
    setCompleting(blockId);
    try {
      await fetchJson(`/postplans/blocks/${blockId}/complete`,{method:'POST'});
      showToast(t('piano.toast_ok'),true);
      load();
    } catch(e){showToast(t('piano.toast_error')+e.message,false);}
    finally{setCompleting(null);}
  };

  const prods = groupByProduction(blocks);
  const allBlocks = prods.flatMap(p=>p.blocks.map(b=>({...b,_prodName:p.production_name,_delivery:p.master_delivery})));
  const activeBlocks = allBlocks.filter(b=>blockStatus(b)==='active');
  const futureBlocks = allBlocks.filter(b=>blockStatus(b)==='future');
  const pastBlocks = allBlocks.filter(b=>blockStatus(b)==='past');
  const activeProds = prods.filter(p=>!isProdDone(p));

  return (
    <div style={{minHeight:'100vh',background:BG,padding:'32px 24px'}}>
      <div style={{maxWidth:1100,margin:'0 auto'}}>
        <div style={{marginBottom:36,display:'flex',justifyContent:'space-between',
          alignItems:'flex-end',flexWrap:'wrap',gap:16}}>
          <div>
            <h1 style={{color:GOLD,fontSize:26,fontWeight:700,margin:0,letterSpacing:'-0.02em'}}>{t('piano.title')}</h1>
            <p style={{color:TEXT_DIM,fontSize:13,margin:'6px 0 0'}}>{t('piano.subtitle')}</p>
          </div>
          {(()=>{
            const upcoming = [...activeBlocks,...futureBlocks].sort((a,b)=>toISO(a.end_date).localeCompare(toISO(b.end_date)));
            const next = upcoming[0];
            if (!next) return null;
            const daysLeft = diffDays(todayISO(), toISO(next.end_date));
            const urgent = daysLeft <= 3;
            return (
              <div style={{textAlign:'right',background:BG3,border:`1px solid ${urgent?'rgba(248,113,113,0.4)':BORDER}`,borderRadius:10,padding:'10px 16px'}}>
                <div style={{color:TEXT_DIM,fontSize:11,marginBottom:2}}>{t('piano.next_deadline')}</div>
                <div style={{color:urgent?RED:'#fff',fontWeight:600,fontSize:14}}>{fmtDateLong(toISO(next.end_date),lang)}</div>
                <div style={{color:TEXT_DIM,fontSize:11,marginTop:2}}>{next.phase_name} EP.{next.episode_no}</div>
              </div>
            );
          })()}
        </div>

        {loading&&(
          <div style={{textAlign:'center',color:TEXT_DIM,padding:80}}>
            <div style={{fontSize:36,marginBottom:12}}>⏳</div>{t('piano.loading')}
          </div>
        )}
        {!loading&&error&&(
          <div style={{background:'rgba(248,113,113,0.1)',border:'1px solid rgba(248,113,113,0.3)',
            borderRadius:10,padding:20,color:'#fca5a5'}}>{t('piano.error_prefix')}{error}</div>
        )}
        {!loading&&!error&&blocks.length===0&&(
          <div style={{textAlign:'center',color:TEXT_DIM,padding:80}}>
            <div style={{fontSize:52,marginBottom:18}}>🎬</div>
            <div style={{fontSize:17,color:'#fff',marginBottom:8,fontWeight:600}}>{t('piano.empty_title')}</div>
            <div style={{fontSize:14,maxWidth:360,margin:'0 auto',lineHeight:1.6}}>
              {t('piano.empty_sub')}
            </div>
          </div>
        )}

        {!loading&&!error&&blocks.length>0&&(<>
          {activeBlocks.length>0&&(
            <section style={{marginBottom:40}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
                <span style={{color:GOLD,fontSize:12,fontWeight:700,letterSpacing:'0.1em'}}>{t('piano.in_corso_label')}</span>
                <div style={{flex:1,height:1,background:`linear-gradient(90deg,${GOLD_MID},transparent)`}}/>
              </div>
              <div style={{display:'grid',
                gridTemplateColumns:activeBlocks.length>1?'repeat(auto-fill,minmax(420px,1fr))':'1fr',gap:16}}>
                {activeBlocks.map(b=>(
                  <ActiveHeroCard key={b.id} block={b} prodName={b._prodName}
                    onComplete={handleComplete} completing={completing}/>
                ))}
              </div>
            </section>
          )}

          {activeBlocks.length===0&&futureBlocks.length>0&&(
            <div style={{background:BG2,border:`1px solid ${BORDER}`,borderRadius:12,
              padding:'20px 24px',marginBottom:32,display:'flex',alignItems:'center',gap:16}}>
              <span style={{fontSize:28}}>👀</span>
              <div>
                <div style={{color:'#fff',fontWeight:600,fontSize:14,marginBottom:2}}>
                  {t('piano.no_active_title')}
                </div>
                <div style={{color:TEXT_DIM,fontSize:13}}>
                  {t('piano.no_active_sub_pre')} {diffDays(todayISO(),toISO(futureBlocks[0].start_date))}{t('piano.no_active_sub_post')}
                  — {futureBlocks[0].phase_name} EP.{futureBlocks[0].episode_no}
                </div>
              </div>
            </div>
          )}

          {futureBlocks.length>0&&(
            <section style={{marginBottom:40}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
                <span style={{color:TEXT_DIM,fontSize:12,fontWeight:700,letterSpacing:'0.1em'}}>
                  {t('piano.future_label')} ({futureBlocks.length})
                </span>
                <div style={{flex:1,height:1,background:'linear-gradient(90deg,rgba(255,255,255,0.07),transparent)'}}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
                {futureBlocks.map(b=><FutureCard key={b.id} block={b} prodName={b._prodName}/>)}
              </div>
            </section>
          )}

          {pastBlocks.length>0&&(
            <section ref={pastRef}>
              <button onClick={()=>{
                const next = !showPast;
                setShowPast(next);
                if (next) setTimeout(()=>pastRef.current?.scrollIntoView({behavior:'smooth',block:'start'}),50);
              }}
                style={{background:'none',border:'none',color:TEXT_DIM,fontSize:12,
                  cursor:'pointer',padding:0,marginBottom:14,
                  display:'flex',alignItems:'center',gap:8,letterSpacing:'0.08em',fontWeight:700}}>
                <span style={{fontSize:10}}>{showPast?'▼':'▶'}</span>
                {t('piano.past_label')} ({pastBlocks.length})
              </button>
              {showPast&&(
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {pastBlocks.map(b=><PastCard key={b.id} block={b} prodName={b._prodName}/>)}
                </div>
              )}
            </section>
          )}
        </>)}
      </div>

      {toast&&(
        <div style={{position:'fixed',bottom:28,left:'50%',transform:'translateX(-50%)',
          background:BG3,border:`1px solid ${toast.ok?GOLD:'rgba(248,113,113,0.5)'}`,
          borderRadius:10,padding:'12px 24px',color:'#fff',fontSize:13,fontWeight:500,
          boxShadow:'0 8px 40px rgba(0,0,0,0.5)',zIndex:9999,whiteSpace:'nowrap'}}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
